import React, { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { useNotifications } from '../../../../../notifications';
import { useNavigate, fillPlaceholders, paths } from '../../../../../routing';
import { queriesV2, mutations } from '../../../../../api';
import {
  convertDocxToDocument,
  convertDocxToDocumentTemplate,
  moveTemplateToFolder,
} from '../../../../../api/mutations';
import { TemplateType } from '../../../../../consts/forms-types';
import FileUploadModal from '../common/FileUploadModal';
import { BaseUploadProps, UploadFileData } from '../common/types';
import { prepareFileForUpload } from '../common/utils';

interface DocxImportContainerProps extends BaseUploadProps {
  templateUpload?: boolean;
}

const DocxImportContainer: React.FC<DocxImportContainerProps> = ({
  isOpen,
  onClose,
  teamId,
  dealId,
  templateUpload = false,
  folders,
  selectedFolderId,
  onFolderSelectionChange,
  foldersLoading,
  isClauseUpload,
}) => {
  const [, { error: notifyError }] = useNotifications();
  const navigate = useNavigate();

  // Deal DOCX import mutation
  const [convertDocxToDocumentMutation, { loading: convertDocxToDocumentLoading }] = useMutation(
    convertDocxToDocument,
    {
      refetchQueries: dealId
        ? [
            {
              query: queriesV2.getDocumentsByDeal,
              variables: { dealId },
            },
          ]
        : [],
    }
  );

  // Template DOCX import mutation
  const [convertDocxToDocumentTemplateMutation, { loading: convertDocxToDocumentTemplateLoading }] = useMutation(
    convertDocxToDocumentTemplate,
    {
      refetchQueries: [
        {
          query: queriesV2.getDocumentTemplates,
          variables: { teamId },
        },
      ],
    }
  );

  const [moveTemplateToFolderMutation] = useMutation(moveTemplateToFolder);
  const [updateTemplateEditorConfiguration] = useMutation(mutations.updateTemplateEditorConfiguration);

  const handleImport = useCallback(
    async ({
      documentTitle,
      selectedFile,
      selectedFiles,
      isRestrictedEditingEnabled,
      isDisableWordExportEnabled,
      selectedFolderId: folderId,
    }: UploadFileData) => {
      try {
        // DOCX import only supports single file
        const file = selectedFile || (selectedFiles && selectedFiles[0]);
        if (!file || !file.fileData) return;

        const fileData = prepareFileForUpload(file);

        if (templateUpload) {
          // Template import
          const result = await convertDocxToDocumentTemplateMutation({
            variables: {
              teamId,
              name: documentTitle,
              templateType: TemplateType.DOCUMENT,
              fileData,
              restrictedEditing: isRestrictedEditingEnabled,
            },
          });

          const templateId = result.data?.convertDocxToDocumentTemplate?.id;

          if (templateId) {
            // Update editor configuration if needed
            if (isDisableWordExportEnabled) {
              await updateTemplateEditorConfiguration({
                variables: {
                  templateId,
                  configuration: {
                    disableWordExport: isDisableWordExportEnabled,
                  },
                },
              });
            }

            // Move to folder if selected
            if (folderId) {
              await moveTemplateToFolderMutation({
                variables: {
                  templateId,
                  folderId,
                },
              });
            }

            // Navigate to template
            const templateUrl = fillPlaceholders(paths.templates.document, {
              templateId,
              teamId,
            });
            navigate(templateUrl);
          }
        } else {
          // Deal document import
          const result = await convertDocxToDocumentMutation({
            variables: {
              dealId: dealId!,
              teamId,
              title: documentTitle,
              fileData,
              configuration: {
                trackChanges: true, // Always enable track changes for Word document imports
                restrictedEditing: false,
              },
            },
          });

          if (result.data?.convertDocxToDocument) {
            const documentId = result.data.convertDocxToDocument.id;
            
            // Navigate to document
            const navigateUrl = fillPlaceholders(paths.documentPath, {
              dealId: dealId!,
              teamId,
              documentId,
            });

            // Note: We don't dispatch to store here as the refetchQueries will update the data
            navigate(navigateUrl);
          }
        }

        onClose();
      } catch (error) {
        notifyError({
          message: {
            id: 'docx-import-error',
            defaultMessage: `DOCX import error: ${error}`,
          },
        });
      }
    },
    [
      convertDocxToDocumentMutation,
      convertDocxToDocumentTemplateMutation,
      moveTemplateToFolderMutation,
      updateTemplateEditorConfiguration,
      templateUpload,
      teamId,
      dealId,
      navigate,
      notifyError,
      onClose,
    ]
  );

  return (
    <FileUploadModal
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={handleImport}
      isLoading={convertDocxToDocumentLoading || convertDocxToDocumentTemplateLoading}
      templateUpload={templateUpload}
      allowMultiple={false} // DOCX import only supports single file
      folders={folders}
      selectedFolderId={selectedFolderId}
      onFolderSelectionChange={onFolderSelectionChange}
      foldersLoading={foldersLoading}
      isClauseUpload={isClauseUpload}
      modalTitle="Import from Word"
      acceptedFileTypes=".docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      showRestrictedEditing={templateUpload}
      showDisableWordExport={templateUpload}
    />
  );
};

export default DocxImportContainer;